'use client';

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ChipProps } from '@mui/material';
import {
  Calendar,
  Clock,
  Repeat,
  Send,
  Pause,
  Play,
  Trash2,
  Edit,
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign
} from 'lucide-react';
import { useToast } from '@crm-src/hooks/use-toast';
import { invoiceSchedulesApi, scheduledInvoicesApi, formatFrequency } from '@crm-src/lib/invoice-schedules-api';
import type { InvoiceSchedule, ScheduledInvoice, UpcomingOccurrence } from '@crm-src/types/invoice-schedule';
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageLoader } from "@/components/ui/page-loader";
import { BackButton } from "@/components/ui/back-button";

// Maps a recurring-schedule status to a MUI Chip color (replaces the former
// Tailwind class string from getScheduleStatusColor during the MUI migration).
function scheduleStatusChipColor(status: string): ChipProps['color'] {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'warning';
    case 'completed':
      return 'primary';
    case 'cancelled':
    default:
      return 'default';
  }
}

interface ScheduleDetail extends InvoiceSchedule {
  scheduled_invoices?: ScheduledInvoice[];
  generated_invoices?: any[];
}

export default function InvoiceScheduleDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const scheduleId = params.id as string;

  const [schedule, setSchedule] = useState<ScheduleDetail | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingOccurrence[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const loadSchedule = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await invoiceSchedulesApi.getById(scheduleId);
      if (response.success && response.data) {
        setSchedule(response.data);
      } else {
        toast({
          title: "Error",
          description: "Schedule not found",
          variant: "destructive",
        });
        navigate(-1);
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      toast({
        title: "Error",
        description: "Failed to load schedule",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [scheduleId, navigate, toast]);

  useEffect(() => {
    if (scheduleId) {
      loadSchedule();
    }
  }, [scheduleId, loadSchedule]);

  // Projected upcoming invoices (numbers + advancing service periods). Only an
  // active schedule has a live projection; failures are non-fatal (the card
  // just doesn't render).
  const loadUpcoming = useCallback(async () => {
    try {
      const res = await invoiceSchedulesApi.preview(scheduleId, 6);
      setUpcoming(res.success && res.data ? res.data.upcoming_invoices || [] : []);
    } catch {
      setUpcoming([]);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (schedule?.status === 'active') {
      loadUpcoming();
    } else {
      setUpcoming([]);
    }
  }, [schedule?.status, loadUpcoming]);

  const handlePause = async () => {
    try {
      setIsActionLoading(true);
      const response = await invoiceSchedulesApi.pause(scheduleId);
      if (response.success) {
        toast({ title: "Success", description: "Schedule paused" });
        loadSchedule();
      } else {
        toast({
          title: "Error",
          description: response.errors?.[0] || "Failed to pause schedule",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to pause schedule", variant: "destructive" });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleResume = async () => {
    try {
      setIsActionLoading(true);
      const response = await invoiceSchedulesApi.resume(scheduleId);
      if (response.success) {
        toast({ title: "Success", description: "Schedule resumed" });
        loadSchedule();
      } else {
        toast({
          title: "Error",
          description: response.errors?.[0] || "Failed to resume schedule",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to resume schedule", variant: "destructive" });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setIsActionLoading(true);
      const response = await invoiceSchedulesApi.delete(scheduleId);
      if (response.success) {
        toast({ title: "Success", description: "Schedule cancelled" });
        navigate('/crm/invoices/schedules');
      } else {
        toast({
          title: "Error",
          description: response.errors?.[0] || "Failed to cancel schedule",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to cancel schedule", variant: "destructive" });
    } finally {
      setIsActionLoading(false);
      setShowCancelDialog(false);
    }
  };

  // Status icon for the schedule Chip. Colors baked to the sRGB hex the old
  // Tailwind text-* utilities produced (green-500/yellow-500/blue-500/gray-500)
  // so the icon tint survives the MUI migration once Tailwind is removed.
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} color="#22c55e" />;
      case 'paused':
        return <Pause size={16} color="#eab308" />;
      case 'completed':
        return <CheckCircle size={16} color="#3b82f6" />;
      case 'cancelled':
        return <XCircle size={16} color="#6b7280" />;
      default:
        return null;
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Timezone-safe short date for date-only ISO strings (service_from/to).
  // Parsing 'YYYY-MM-DD' via `new Date()` treats it as UTC midnight and can
  // shift a day in negative-offset zones — parse the parts explicitly instead.
  const formatShortDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return dateStr;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const servicePeriodText = (o: UpcomingOccurrence) =>
    o.service_from && o.service_to
      ? `${formatShortDate(o.service_from)} – ${formatShortDate(o.service_to)}`
      : '-';

  const getScheduledInvoiceStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip size="small" variant="outlined" color="primary" icon={<Clock size={12} />} label="Pending" />;
      case 'sent':
        return <Chip size="small" color="success" icon={<CheckCircle size={12} />} label="Sent" />;
      case 'failed':
        return <Chip size="small" color="error" icon={<AlertTriangle size={12} />} label="Failed" />;
      case 'cancelled':
        return <Chip size="small" color="default" icon={<XCircle size={12} />} label="Cancelled" />;
      default:
        return <Chip size="small" variant="outlined" label={status} />;
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (!schedule) {
    return (
      <Box sx={{ py: 4 }}>
        <Card>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              <Typography>Schedule not found</Typography>
              <BackButton sx={{ mt: 2 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const isActive = schedule.status === 'active';
  const isPaused = schedule.status === 'paused';
  const isCompleted = schedule.status === 'completed';
  const isCancelled = schedule.status === 'cancelled';
  const statusIcon = getStatusIcon(schedule.status);

  return (
    <Box sx={{ py: 4, maxWidth: 1024, mx: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BackButton />
          <Box>
            <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
              <Repeat size={24} />
              {schedule.name}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {formatFrequency(schedule.frequency, schedule.interval)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            size="small"
            color={scheduleStatusChipColor(schedule.status) ?? "default"}
            {...(statusIcon ? { icon: statusIcon } : {})}
            label={schedule.status}
            sx={{ textTransform: "capitalize" }}
          />
          {!isCompleted && !isCancelled && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isActive && (
                <Button variant="outlined" color="inherit" size="small" onClick={handlePause} disabled={isActionLoading} startIcon={<Pause size={16} />}>
                  Pause
                </Button>
              )}
              {isPaused && (
                <Button variant="outlined" color="inherit" size="small" onClick={handleResume} disabled={isActionLoading} startIcon={<Play size={16} />}>
                  Resume
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setShowCancelDialog(true)}
                disabled={isActionLoading}
                startIcon={<Trash2 size={16} />}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" }, gap: 3 }}>
        {/* Main Details */}
        <Box sx={{ gridColumn: { lg: "span 2" }, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Schedule Info Card */}
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Calendar size={20} />
                  <Typography component="span" sx={{ fontWeight: 600 }}>Schedule Details</Typography>
                </Box>
              }
            />
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Next Invoice */}
              {schedule.next_scheduled_at && isActive && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.08), borderRadius: 1 }}>
                  <Box sx={{ color: "primary.main", mt: "2px", display: "flex" }}><Clock size={20} /></Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Next Invoice</Typography>
                    <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "primary.dark" }}>
                      {formatDate(schedule.next_scheduled_at)}
                    </Typography>
                    {/* Invoices bill in arrears — name the period that date
                        closes, so the issue date never reads as premature. The
                        period END is deliberately not derived from the issue
                        date: they coincide only at the default 1-day lead. */}
                    {schedule.next_period_start_at && (
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        for the period starting {formatDate(schedule.next_period_start_at)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Box sx={{ color: "text.disabled", mt: "2px", display: "flex" }}><Repeat size={20} /></Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Frequency</Typography>
                    <Typography sx={{ fontWeight: 500 }}>{formatFrequency(schedule.frequency, schedule.interval)}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Box sx={{ color: "text.disabled", mt: "2px", display: "flex" }}><Calendar size={20} /></Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Start Date</Typography>
                    <Typography sx={{ fontWeight: 500 }}>{formatDate(schedule.start_date)}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Box sx={{ color: "text.disabled", mt: "2px", display: "flex" }}><FileText size={20} /></Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Generated</Typography>
                    <Typography sx={{ fontWeight: 500 }}>{schedule.occurrences_generated || 0} invoices</Typography>
                  </Box>
                </Box>
              </Box>

              {schedule.description && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Box sx={{ color: "text.disabled", mt: "2px", display: "flex" }}><FileText size={20} /></Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>Description</Typography>
                    <Typography sx={{ fontSize: 14 }}>{schedule.description}</Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Invoices projection — numbers + advancing service periods */}
          {upcoming.length > 0 && (
            <Card>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Clock size={20} />
                    <Typography component="span" sx={{ fontWeight: 600 }}>Upcoming Invoices</Typography>
                  </Box>
                }
                subheader="Projected numbers, issue/due dates, and service periods for the next occurrences"
              />
              <CardContent>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small" data-testid="upcoming-invoices-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Invoice&nbsp;#</TableCell>
                        <TableCell>Issue Date</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Service Period</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcoming.map((o) => (
                        <TableRow key={o.occurrence_number}>
                          <TableCell>{o.occurrence_number}</TableCell>
                          <TableCell>
                            {o.invoice_number || (
                              <Typography component="span" sx={{ fontStyle: "italic", color: "text.secondary", fontSize: 13 }}>
                                at generation
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatShortDate(o.issue_date)}</TableCell>
                          <TableCell>{formatShortDate(o.due_date)}</TableCell>
                          <TableCell>{servicePeriodText(o)}</TableCell>
                          <TableCell align="right">${Number(o.amount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Generated Invoices History */}
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FileText size={20} />
                  <Typography component="span" sx={{ fontWeight: 600 }}>Invoice History</Typography>
                </Box>
              }
              subheader="Invoices generated from this recurring schedule"
            />
            <CardContent>
              {schedule.scheduled_invoices && schedule.scheduled_invoices.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schedule.scheduled_invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{formatDate(inv.scheduled_date)}</TableCell>
                        <TableCell>
                          {inv.generated_invoice?.invoice_number || inv.source_invoice?.invoice_number || '-'}
                        </TableCell>
                        <TableCell>
                          ${Number(inv.generated_invoice?.total_amount || inv.source_invoice?.total_amount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{getScheduledInvoiceStatusBadge(inv.status)}</TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            color="inherit"
                            size="small"
                            onClick={() => navigate(`/crm/invoices/scheduled/${inv.id}`)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1.5 }}><FileText size={48} /></Box>
                  <Typography>No invoices generated yet</Typography>
                  {schedule.next_scheduled_at && (
                    <Typography sx={{ fontSize: 14, mt: 0.5 }}>
                      First invoice scheduled for {formatDate(schedule.next_scheduled_at)}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Customer Card */}
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <User size={16} />
                  <Typography component="span" sx={{ fontWeight: 600, fontSize: "1rem" }}>Customer</Typography>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  {schedule.customer?.full_name || schedule.customer?.company_name || 'Unknown'}
                </Typography>
                {schedule.customer?.company_name && schedule.customer?.full_name && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{schedule.customer.company_name}</Typography>
                )}
                {schedule.customer?.email && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{schedule.customer.email}</Typography>
                )}
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  sx={{ width: "100%", mt: 1.5 }}
                  onClick={() => navigate(`/crm/customers/${schedule.customer?.id}`)}
                >
                  View Customer
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Source Invoice Card */}
          {schedule.source_invoice && (
            <Card>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FileText size={16} />
                    <Typography component="span" sx={{ fontWeight: 600, fontSize: "1rem" }}>Source Invoice</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Invoice Number</Typography>
                    <Typography sx={{ fontWeight: 500 }}>{schedule.source_invoice.invoice_number}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Amount</Typography>
                    <Typography sx={{ fontWeight: 600, color: "success.main", fontSize: "1.125rem" }}>
                      ${Number(schedule.source_invoice.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    sx={{ width: "100%" }}
                    onClick={() => navigate(`/crm/invoices/${schedule.source_invoice?.id}`)}
                  >
                    View Source Invoice
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Payment Terms */}
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DollarSign size={16} />
                  <Typography component="span" sx={{ fontWeight: 600, fontSize: "1rem" }}>Payment Terms</Typography>
                </Box>
              }
            />
            <CardContent>
              <Typography sx={{ fontWeight: 500 }}>
                Net {schedule.payment_terms_days || 30} days
              </Typography>
              <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
                Due date calculated from invoice issue date
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        title="Cancel Recurring Schedule?"
        description="This will permanently cancel this recurring schedule. No more invoices will be generated. Existing generated invoices will not be affected."
        confirmLabel="Yes, Cancel Schedule"
        cancelLabel="Keep Schedule"
        onConfirm={handleCancel}
      />
    </Box>
  );
}
